import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Building2, Mail, Pencil, X } from 'lucide-react'

import { crmErrorDetail, crmService, type CrmContact } from '@/services/crm'
import type { RelationshipDefinitions, RelationshipRow } from '@/services/crmRelationships'
import { formatUsd } from '@/utils/money'
import { mailtoHref } from '../contactLinks'
import { distinctCommunications, formatStamp } from '../crmFields'
import { NEEDS_REVIEW_TITLE, NOT_ON_RECORD, contactInitials, relativeTime, typeBadgeColor } from '../crmFormat'
import HealthRing from './HealthRing'
import { BAND_STYLE, STAGE_STYLE, healthDescription, recentItems, volumeCoverage } from './relationshipFormat'

const BUTTON =
  'inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-[11px] font-medium focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50'

/**
 * The peek: the selected person at a glance, beside the board.
 *
 * Every figure is the server's row or a count of the record already in hand,
 * so selecting a card costs nothing. The one thing fetched on selection is the
 * contact's own record trail (capture, edits, merges), lazily and only five
 * lines of it. The full profile stays one button away and is opened through
 * the same window the table opens.
 */
export default function RelationshipPeek({
  contact,
  row,
  definitions,
  onClose,
  onOpenProfile,
  onEdit,
}: {
  contact: CrmContact
  row: RelationshipRow | undefined
  definitions?: RelationshipDefinitions
  onClose: () => void
  onOpenProfile: (id: string) => void
  onEdit: (contact: CrmContact) => void
}) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const entity = contact.kind === 'organisation' || contact.isEntity
  const stage = row?.stage ?? null
  const style = stage ? STAGE_STYLE[stage] : null
  const email = mailtoHref(contact.email)
  const messages = useMemo(() => distinctCommunications(contact.communications ?? []).length, [contact.communications])
  const recent = useMemo(() => recentItems(contact), [contact])

  const trail = useQuery({
    queryKey: ['crm', 'activity', 'contact', contact.id, 5],
    queryFn: () => crmService.getContactActivity(contact.id, 5),
    staleTime: 30_000,
    retry: false,
  })

  const confirm = useMutation({
    mutationFn: () => crmService.confirmContact(contact.id),
    onSuccess: async () => {
      setNote({ tone: 'ok', text: 'Confirmed.' })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
      ])
    },
    onError: (failure) => setNote({ tone: 'error', text: crmErrorDetail(failure, 'The contact could not be confirmed.') }),
  })

  const stageRule = definitions?.stages.find((s) => s.key === stage)?.rule
  const stats: { label: string; value: string; sub: string; title?: string }[] = row
    ? [
        { label: 'Deals', value: String(row.deals), sub: `${row.deals12m} in the last 12 months` },
        {
          label: 'Volume',
          value: row.volume !== null ? formatUsd(row.volume) : NOT_ON_RECORD,
          sub: volumeCoverage(row),
        },
        {
          label: 'Funded',
          value: row.dealsWithStage > 0 ? String(row.funded) : NOT_ON_RECORD,
          sub: row.dealsWithStage > 0 ? `of ${row.dealsWithStage} with a stage` : 'No deal states a stage',
          title: row.dealsWithStage > 0 ? `${row.funded} of ${row.dealsWithStage} deals with a stage on record are funded` : undefined,
        },
        { label: 'Messages', value: String(messages), sub: `${row.touches90d} in the last 90 days` },
      ]
    : []

  return (
    <aside
      aria-label="Selected relationship"
      className="flex w-full min-w-0 flex-none flex-col gap-4 overflow-y-auto border-l border-amber-400/10 bg-black/20 px-4 py-4 scrollbar-thin md:w-[320px]"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className={clsx(
            'flex h-12 w-12 flex-none items-center justify-center border border-amber-400/20 bg-amber-400/[0.08] font-display text-[18px] text-amber-200',
            entity ? 'rounded-lg' : 'rounded-full',
          )}
        >
          {entity ? <Building2 size={18} /> : contactInitials(contact)}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[26px] font-semibold leading-[1.05] text-stone-50">{contact.displayName}</h2>
          <div className="mt-0.5 truncate text-[12px] text-stone-400" title={[contact.title, contact.company].filter(Boolean).join(', ')}>
            {[contact.title, contact.company].filter(Boolean).join(', ') || NOT_ON_RECORD}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the relationship panel"
          className="rounded p-1 text-stone-500 hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={clsx('rounded-full border px-2 py-0.5 text-[10px] font-medium', typeBadgeColor(contact.companyType))}>
          {contact.companyType}
        </span>
        {style ? (
          <span className={clsx('inline-flex items-center gap-1.5 text-[12px]', style.text)}>
            <span className={clsx('h-2 w-2 rounded-full', style.dot)} aria-hidden="true" />
            {style.title}
          </span>
        ) : (
          <span className="text-[11px] italic text-stone-500">
            {row?.staff ? 'Desk staff: not staged' : 'No stage'}
          </span>
        )}
        {contact.needsReview ? (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-400/25 bg-amber-400/[0.08] px-1.5 py-0.5 text-[10px] text-amber-200"
            title={NEEDS_REVIEW_TITLE}
          >
            Not confirmed
            <button
              type="button"
              onClick={() => confirm.mutate()}
              disabled={confirm.isPending}
              className="rounded border border-amber-400/30 px-1 text-[10px] font-medium hover:bg-amber-400/[0.12] disabled:text-stone-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
            >
              {confirm.isPending ? 'Confirming…' : 'Confirm'}
            </button>
          </span>
        ) : null}
      </div>
      {note ? (
        <div role="status" className={clsx('text-[11px]', note.tone === 'error' ? 'text-red-300' : 'text-stone-400')}>
          {note.text}
        </div>
      ) : null}

      {row ? (
        <>
          <div className="rounded-lg border border-white/[0.08] p-3">
            <div className="flex items-center gap-3">
              <HealthRing row={row} size={64} className="flex-none" />
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-stone-100">
                  Relationship health
                  {row.healthBand ? (
                    <span className={clsx('ml-2 text-[11px] font-medium', BAND_STYLE[row.healthBand].text)}>
                      {BAND_STYLE[row.healthBand].word}
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] text-stone-500">
                  {row.health !== null
                    ? 'Recency, deal flow and recent activity, added up. Each part is below.'
                    : healthDescription(row)}
                </div>
              </div>
            </div>
            {row.healthParts && definitions ? (
              <dl className="mt-3 space-y-1" aria-label="Health parts">
                {definitions.health.parts.map((part) => (
                  <div key={part.key} className="flex items-center gap-2 text-[11px]">
                    <dt className="w-[92px] flex-none text-stone-500">{part.label}</dt>
                    <dd className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="h-1 flex-1 overflow-hidden rounded bg-stone-800">
                        <span
                          className="block h-full bg-amber-400/70"
                          style={{ width: `${Math.min(100, (row.healthParts![part.key] / part.max) * 100)}%` }}
                        />
                      </span>
                      <span className="w-[44px] flex-none text-right font-mono tabular-nums text-stone-300">
                        {Math.round(row.healthParts![part.key])}/{part.max}
                      </span>
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {definitions ? (
              <details className="mt-3 text-[11px] text-stone-500">
                <summary className="cursor-pointer text-stone-400 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60">
                  How this is decided
                </summary>
                <p className="mt-1.5 leading-relaxed">{definitions.health.summary}</p>
                {stageRule ? (
                  <p className="mt-1.5 leading-relaxed">
                    <span className="text-stone-400">{style?.title}: </span>
                    {stageRule}
                  </p>
                ) : null}
              </details>
            ) : null}
          </div>

          {row.reason ? (
            <p className={clsx('text-[12px] leading-snug', stage === 'cooling' ? 'text-orange-300/90' : 'text-stone-300')}>
              {row.reason}
            </p>
          ) : null}

          <dl className="grid grid-cols-2 gap-2" aria-label="Relationship figures">
            {stats.map((stat) => (
              <div key={stat.label} className="min-w-0 rounded-md border border-white/[0.08] px-2.5 py-2">
                <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">{stat.label}</dt>
                <dd
                  className={clsx(
                    'mt-0.5 truncate text-[15px] font-semibold tabular-nums',
                    stat.value === NOT_ON_RECORD ? 'text-[12px] font-normal italic text-stone-600' : 'text-stone-100',
                  )}
                  title={stat.title ?? stat.value}
                >
                  {stat.value}
                </dd>
                <dd className="truncate text-[10px] text-stone-500" title={stat.sub}>
                  {stat.sub}
                </dd>
              </div>
            ))}
          </dl>

          {row.suggestion ? (
            <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-amber-300">Why this was flagged</div>
              <p className="mt-1.5 text-[12px] leading-snug text-stone-200">{row.suggestion}</p>
              {definitions ? (
                <p className="mt-1.5 text-[10.5px] leading-snug text-stone-500">
                  Rule: {definitions.stages.find((s) => s.key === 'cooling')?.rule}
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-[12px] italic text-stone-500">
          This record is not on the relationship board (it is retired, or the board could not be read).
        </p>
      )}

      <section aria-label="Recent activity">
        <div className="mb-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Recent</div>
        {recent.length === 0 ? (
          <div className="text-[11px] italic text-stone-600">No dated message or deal on record.</div>
        ) : (
          <ul className="divide-y divide-white/[0.05]">
            {recent.map((item) => (
              <li key={item.key} className="flex gap-3 py-1.5 text-[11.5px]">
                <span className="w-[52px] flex-none text-stone-500" title={formatStamp(item.when)}>
                  {relativeTime(item.when)}
                </span>
                <span className="min-w-0 text-stone-300">{item.label}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Record history">
        <div className="mb-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Record history</div>
        {trail.isPending ? (
          <div className="text-[11px] text-stone-600">Reading the record…</div>
        ) : trail.isError ? (
          <div role="alert" className="text-[11px] text-red-300">
            {crmErrorDetail(trail.error, 'The record history could not be read.')}
          </div>
        ) : trail.data.items.length === 0 ? (
          <div className="text-[11px] italic text-stone-600">Nothing has been recorded about this contact yet.</div>
        ) : (
          <ul className="divide-y divide-white/[0.05]">
            {trail.data.items.map((item) => (
              <li key={item.id} className="flex gap-3 py-1.5 text-[11.5px]">
                <span className="w-[52px] flex-none text-stone-500" title={formatStamp(item.createdAt)}>
                  {relativeTime(item.createdAt)}
                </span>
                <span className="min-w-0 text-stone-300">{item.summary}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onOpenProfile(contact.id)}
          className={clsx(BUTTON, 'flex-1 border-amber-400/40 bg-amber-400/[0.12] text-amber-100 hover:bg-amber-400/[0.2]')}
        >
          Open full profile
        </button>
        <button
          type="button"
          onClick={() => onEdit(contact)}
          className={clsx(BUTTON, 'border-amber-400/15 text-stone-300 hover:border-amber-400/30 hover:text-stone-100')}
        >
          <Pencil size={11} aria-hidden="true" /> Edit
        </button>
        {email ? (
          <a
            href={email}
            className={clsx(BUTTON, 'border-amber-400/15 text-stone-300 hover:border-amber-400/30 hover:text-stone-100')}
          >
            <Mail size={11} aria-hidden="true" /> Email
          </a>
        ) : null}
      </div>
    </aside>
  )
}
